import React from 'react';
import { Tab } from 'react-bootstrap';
import { ContentMessage } from 'axway-react-ui';
import NodeConfigField from './NodeConfigField';

/**
 * Checks if the specific field has an invalidation.
 *
 * @param {string} type - One of: outputsInvalid, paramsInvalid,
 * or authorizationInvalid
 * @param {string} name - Name of the field to check if invalid.
 * @param {object} [errors] - Validation errors.
 * @return {object} Return object if invalid.
 */
function getFieldValidation({ type, name, errors }) {
	return errors && errors[type] && errors[type][name];
}

/**
 * Renders an inputs `Tab` containing a list of `NodeConfigField` generated
 * from flow-node `parameter` properties.  That said, the implementation of
 * this is inspecific to the flow-node parameter, and is currently equivalent
 * to a JSON schema (i.e. it's not using `initialType`).  All values are
 * currently read-only.
 *
 * @param {object} options - The options.
 * @param {string} options.title - The title of the tab.
 * @param {object} options.inputs - A simple object of key:value pairs that
 * represent values for the properties defined in `parameter`.
 * @param {object} options.parameter - A flow-node parameter properties
 * definition, e.g. from a method.
 * @param {string} options.emptyMessage - The message to display if there
 * are no inputs.
 * @param {Tab} A tab with input properties.
 */
function renderTriggerInputsTab(options) {
	let { title } = options;
	const {
		description,
		inputs,
		parameter,
		emptyMessage,
		errors,
		onChange,
		onEnable,
		selectors
	} = options;

	const invalidInputParams = errors && !!errors.inputParamsInvalid;

	if (invalidInputParams) {
		title = (
			<div>
				{title}{<span className="node-status-icon gg icon-GG-warning" />}
			</div>
		);
	}

	// If there are no flow inputs, still render the Tab, but notify the user
	// there are no configurable inputs.
	if (!parameter || !Object.keys(parameter.properties).length) {
		return (
			<Tab
				eventKey="inputs"
				title={title}
			>
				<ContentMessage
					detail={emptyMessage}
					imgClass="gg icon-GG-empty-list"
					className="sm empty"
					inlineContent
				/>
			</Tab>
		);
	}

	let fields = [];
	for (const id in parameter.properties) {
		const param = parameter.properties[id];
		// parameter.required is a list of IDs - i.e. ['config', 'env', ...]
		const required = parameter.required && parameter.required.includes(id);
		const invalid = getFieldValidation({
			type: 'inputParamsInvalid',
			name: id,
			errors
		});

		// Disable editing of the fields if we don't have the onChange. We
		// remove the onChange in the TriggerConfig if the plugin is uninstalled
		// and we can use that to decide if edits are permitted or not.
		const readOnly = !onChange;
		const enabled = inputs.hasOwnProperty(id);

		fields.push(
			<NodeConfigField
				id={id}
				key={id}
				name={param.title || id}
				description={param.description}
				valueType="jsonpath" // Always treat these as jsonpath
				inputTypes={[]} // Hides the input selection
				value={inputs[id] || ''}
				required={required}
				enabled={enabled}
				schema={param}
				selectors={selectors}
				invalid={invalid}
				onChange={onChange}
				onEnable={onEnable}
				showSchema
				readOnly={readOnly}
			/>
		);
	}

	return (
		<Tab eventKey="inputs" title={title} >
			<div className="nodeconfigfield form-group">
				{description}
			</div>
			{fields}
		</Tab>
	);
}

export default renderTriggerInputsTab;
